import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useForm, standardSchemaResolver, UseFormReturn, useWatch } from '@fiverr-private/forms';
import { services, useIdentificationContext } from '@fiverr-private/identification_core';
import { createProfileSetupSchema, type ProfileSetupFormSchema } from '../ProfileSetupStep.schema';
import { translate } from '../../../../utils/translate';
import { VALIDATION_DEBOUNCE_TIME } from '../../common/constants';

interface ValidateArgs {
    value: string;
    methods: UseFormReturn<ProfileSetupFormSchema>;
}

export const useProfileForm = () => {
    const [loading, setLoading] = useState(false);
    const [isUsernameAvailable, setUsernameAvailable] = useState(false);
    const { isSocialFlow, username: initialUsername } = useIdentificationContext();

    const methods = useForm<ProfileSetupFormSchema>({
        mode: 'onSubmit',
        resolver: standardSchemaResolver(createProfileSetupSchema() as any),
        defaultValues: {
            firstName: '',
            lastName: '',
            username: isSocialFlow ? initialUsername : '',
        },
    });

    const [firstName, lastName, username] = useWatch({
        control: methods.control,
        name: ['firstName', 'lastName', 'username'],
    });

    useEffect(() => {
        const fieldsToValidate = ['firstName', 'lastName'] satisfies (keyof ProfileSetupFormSchema)[];

        const validateField = async (fieldName: keyof ProfileSetupFormSchema) => {
            const { isTouched } = methods.getFieldState(fieldName);
            if (isTouched) {
                await methods.trigger(fieldName);
            }
        };

        fieldsToValidate.forEach(validateField);
    }, [firstName, lastName, username, methods]);

    const verifyUsernameDebounced = useMemo(() => {
        const verifyUsername = async ({ value, methods }: ValidateArgs) => {
            setLoading(true);
            setUsernameAvailable(false);

            const usernameFormatValid = await methods.trigger('username');
            if (!usernameFormatValid) {
                setLoading(false);
                return;
            }

            const { errorKeys } = await services.validateUsernameForProfileSetup(value);
            setLoading(false);
            const errorKey = errorKeys?.username?.[0];
            if (errorKey) {
                methods.setError('username', {
                    message: translate(`validation.username.${errorKey}`),
                });
                return;
            }

            methods.clearErrors('username');
            setUsernameAvailable(true);
        };

        return debounce(verifyUsername, VALIDATION_DEBOUNCE_TIME);
    }, []);

    const onUsernameChange = async (value: string) => {
        verifyUsernameDebounced.cancel();
        methods.clearErrors('username');
        setLoading(true);
        setUsernameAvailable(false);

        await verifyUsernameDebounced({ value, methods });
    };

    useEffect(() => {
        const cleanupOnUnmount = () => verifyUsernameDebounced.cancel();
        return cleanupOnUnmount;
    }, [verifyUsernameDebounced]);

    return {
        loading,
        setLoading,
        methods,
        isUsernameAvailable,
        onUsernameChange,
    };
};
